import { useCallback, useEffect, useState } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Button, Checkbox, Divider, Header, Icon, Input, Message, Segment, Select } from "semantic-ui-react"
import { languages } from "simplydo/core/i18n"
import styled from "styled-components"

import actions from "actions"
import api from "api"
import constants from "utils/constants"
import util from "utils/utils"

import IpRestriction from "./IpRestriction"
import PointOfContact from "./PointOfContact"

export const SegmentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  gap: 12px;
  margin-bottom: 12px;

  .ui.segment {
    margin: 0;
    flex: 1 0 40%;
  }
`

const possibleMethods = [
  { key: "email", icon: "mail" },
  { key: "push", icon: "mobile" },
]

const deletionTimeframe = [
  { key: "never", text: "Never" },
  { key: 183, text: "After 6 months of inactivity" },
  { key: 365, text: "After 12 months of inactivity" },
  { key: 730, text: "After 24 months of inactivity" },
]

const ideas = constants.subscriptions.filter((s) => s.type === "ideas")
const challenges = constants.subscriptions.filter((s) => s.type === "challenges")
const groups = constants.subscriptions.filter((s) => s.type === "groups")
const organisations = constants.subscriptions.filter((s) => s.type === "organisations")
const general = constants.subscriptions.filter((s) => s.type === "general")

function Subscription({ sub, hasSub, toggleSub }) {
  const { t } = useTranslation()

  return (
    <div
      key={sub.key}
      style={{
        marginTop: 5,
        marginBottom: 5,
        paddingTop: 3,
        paddingBottom: 3,
        borderBottom: "1px solid #e9ebee",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {t(sub.text)}
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        {possibleMethods.map((m) => (
          <Checkbox
            disabled={sub.unsupportedMethods?.indexOf(m.key) > -1}
            data-tooltip={sub.unsupportedMethods?.indexOf(m.key) > -1 ? "This method is not available" : null}
            style={{ margin: 5 }}
            key={m.key}
            checked={hasSub(sub.key, m.key)}
            onClick={(_e, c) => toggleSub(sub.key, m.key, c.checked)}
          />
        ))}
      </div>
    </div>
  )
}

function NotificationSectionHeader({ title }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "bottom",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <Header style={{ display: "block", marginBottom: 0 }}>
        <h5>{title}</h5>
      </Header>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        {possibleMethods.map((m) => (
          <Icon key={m.key} name={m.icon} style={{ margin: 5 }} />
        ))}
      </div>
    </div>
  )
}

const SettingsGeneral = ({ user, onOrganisationEdited }) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const [userIp, setUserIp] = useState("")
  const { ownerOrganisation } = user
  const orgId = ownerOrganisation?._id

  const { t } = useTranslation()

  useEffect(() => {
    if (showErrorMessage) {
      window.scrollTo(0, 0)
    }
  }, [showErrorMessage])

  const getUserIpInfo = useCallback(() => {
    api.organisations.getIpAddress(orgId, ({ ip }) => {
      setUserIp(ip)
    })
  }, [orgId])

  const saveName = useCallback(() => {
    setShowErrorMessage(false)
    api.organisations.updateSettings(
      ownerOrganisation._id,
      { name: ownerOrganisation.name },
      () => toast.success(t("generic.typeUpdated", { type: "Settings" })),
      () => setShowErrorMessage(true),
    )
  }, [t, ownerOrganisation])

  useEffect(() => {
    getUserIpInfo()
  }, [getUserIpInfo])

  const updateUserAccountSelfDeletion = useCallback(
    (enabled) => {
      setShowErrorMessage(false)
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { disableAccountSelfDeletion: !enabled },
        () => {
          onOrganisationEdited("disableAccountSelfDeletion", !enabled)
          toast.success(t("generic.typeUpdated", { type: "Settings" }))
        },
        () => setShowErrorMessage(true),
      )
    },
    [t, ownerOrganisation, onOrganisationEdited],
  )

  const changeDefaultLanguage = useCallback(
    (defaultLanguage) => {
      setShowErrorMessage(false)
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultLanguage },
        () => {
          onOrganisationEdited("defaultLanguage", defaultLanguage)
          toast.success(t("generic.typeUpdated", { type: "Settings" }))
        },
        () => setShowErrorMessage(true),
      )
    },
    [t, ownerOrganisation, onOrganisationEdited],
  )

  const hasSub = useCallback(
    (key, method) => {
      const subs = ownerOrganisation.defaultSubscriptions || {}
      const sub = subs[key]
      return sub && sub.indexOf(method) > -1
    },
    [ownerOrganisation],
  )

  const toggleSub = useCallback(
    (key, method, checked) => {
      const subs = ownerOrganisation.defaultSubscriptions || {}
      let methods = subs[key] || []
      if (!methods.length && checked) methods = [method]
      if (methods.length) {
        const index = methods.indexOf(method)
        if (checked && index === -1) methods.push(method)
        if (!checked && index > -1) methods.splice(index, 1)
      }
      subs[key] = methods
      setShowErrorMessage(false)
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultSubscriptions: subs },
        () => {
          onOrganisationEdited("defaultSubscriptions", subs)
        },
        () => setShowErrorMessage(true),
      )
    },
    [ownerOrganisation, onOrganisationEdited],
  )

  const toggleNotifications = useCallback(
    (enabled) => {
      setShowErrorMessage(false)
      if (enabled) {
        util
          .confirm(
            "Default notifications",
            "Are you sure you want to edit the default notifications of your organisation? If you do so, all new users will start with the email/push notification settings that you choose.",
          )
          .then(
            () => {
              api.organisations.updateSettings(
                ownerOrganisation._id,
                { defaultSubscriptions: constants.defaultSubscriptions },
                () => {
                  onOrganisationEdited("defaultSubscriptions", constants.defaultSubscriptions)
                },
                () => setShowErrorMessage(true),
              )
            },
            () => {},
          )
          .catch(() => {})
      } else {
        api.organisations.updateSettings(
          ownerOrganisation._id,
          { defaultSubscriptions: enabled },
          () => {
            onOrganisationEdited("defaultSubscriptions", enabled)
          },
          () => setShowErrorMessage(true),
        )
      }
    },
    [ownerOrganisation, onOrganisationEdited],
  )

  const changeAutoDeletionTime = useCallback(
    (value) => {
      setShowErrorMessage(false)
      if (value === "never") value = null
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { autoAccountDeletionPolicy: { deleteAfter: value } },
        () => {
          onOrganisationEdited("autoAccountDeletionPolicy", { deleteAfter: value })
        },
        () => setShowErrorMessage(true),
      )
    },
    [ownerOrganisation, onOrganisationEdited],
  )

  return (
    <div>
      {showErrorMessage ? (
        <Message error>
          <Message.Header>There was a problem</Message.Header>
          <Message.Content>
            We were unable to save your settings. You may not have the correct permissions. Please contact an
            administrator for further advice.
          </Message.Content>
        </Message>
      ) : null}

      <SegmentContainer>
        <Segment>
          <h3>{t("admin.settings.general.organisation.title")}</h3>
          <p>{t("admin.settings.general.organisation.info")}</p>
          <Input
            fluid
            value={user.ownerOrganisation.name}
            onChange={(e) => onOrganisationEdited("name", e.target.value)}
            action={<Button primary content={t("generic.save")} onClick={saveName} />}
          />
        </Segment>
        <Segment>
          <h3>{t("admin.settings.general.language.title")}</h3>
          <p>{t("admin.settings.general.language.info", { user })}</p>
          <Select
            fluid
            value={user.ownerOrganisation.defaultLanguage || "en"}
            options={languages.map((l) => ({ key: l.key, value: l.key, text: l.name }))}
            onChange={(_e, s) => changeDefaultLanguage(s.value)}
          />
        </Segment>
      </SegmentContainer>

      <SegmentContainer>
        <Segment>
          <h3>Automatically delete inactive users</h3>
          <p>
            {util.appName()} can automatically delete user accounts in {user?.ownerOrganisation?.name} according to a
            schedule that you customise.
          </p>
          <p>
            Choose an option below to schedule user accounts to be deleted after the selected time has elapsed since
            their last-seen date. Users will be sent warning emails a few weeks before their accounts are deleted.
          </p>
          <Message warning size="small">
            <strong>Please note:</strong> {t("generic.ideas")}, messages, and comments belonging to affected user
            accounts will also be deleted.
          </Message>
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <p style={{ marginRight: 10 }}>
              <strong>Delete user accounts</strong>
            </p>
            <Select
              value={user.ownerOrganisation?.autoAccountDeletionPolicy?.deleteAfter || "never"}
              options={deletionTimeframe.map((l) => ({ key: l.key, value: l.key, text: l.text }))}
              onChange={(_e, s) => changeAutoDeletionTime(s.value)}
            />
          </div>
        </Segment>

        <Segment>
          <h3>Allow users to delete their own accounts</h3>
          <p>
            By default in {util.appName()}, users can delete their own accounts. By disabling this, users will no longer
            be able to delete their own accounts, and only administrators will be able to delete users from{" "}
            {util.appName()}.
          </p>
          <Checkbox
            toggle
            label="Allow users to delete their own accounts"
            checked={!user.ownerOrganisation?.disableAccountSelfDeletion}
            onChange={(_e, c) => updateUserAccountSelfDeletion(c.checked)}
          />
        </Segment>
      </SegmentContainer>

      <SegmentContainer>
        <Segment>
          <h3>Default notification subscriptions</h3>
          <p>
            {util.appName()} will automatically assign a default set of notification subscriptions to new users as they
            join {user.ownerOrganisation.name}. If you prefer, you can configure your own default notification
            subscriptions below by choosing when users should receive emails and push notifications.
          </p>
          <Checkbox
            style={{ marginBottom: 10 }}
            toggle
            label="Customise event subscriptions"
            checked={!!user.ownerOrganisation.defaultSubscriptions}
            onChange={(_e, c) => toggleNotifications(c.checked)}
          />

          {user.ownerOrganisation.defaultSubscriptions && (
            <div>
              <p>
                To reset to {util.appName()}'s default notification subscriptions set, please deselect the toggle above.
              </p>
              <NotificationSectionHeader title={t("users.preferences.yourAccount.title")} />
              {general.map((sub) => (
                <Subscription key={sub.key} sub={sub} hasSub={hasSub} toggleSub={toggleSub} />
              ))}

              <Divider hidden />
              <NotificationSectionHeader title="Groups" />
              {groups.map((sub) => (
                <Subscription key={sub.key} sub={sub} hasSub={hasSub} toggleSub={toggleSub} />
              ))}

              <Divider hidden />
              <NotificationSectionHeader title={t("common:capitalise", { key: "generic.ideas" })} />
              {ideas.map((sub) => (
                <Subscription key={sub.key} sub={sub} hasSub={hasSub} toggleSub={toggleSub} />
              ))}

              <Divider hidden />
              <NotificationSectionHeader title={t("users.preferences.yourChallenges.title")} />
              {challenges.map((sub) => (
                <Subscription key={sub.key} sub={sub} hasSub={hasSub} toggleSub={toggleSub} />
              ))}

              <Divider hidden />
              <NotificationSectionHeader title="Organisation" />
              {organisations.map((sub) => (
                <Subscription key={sub.key} sub={sub} hasSub={hasSub} toggleSub={toggleSub} />
              ))}
            </div>
          )}
        </Segment>

        <IpRestriction userIp={userIp} />
      </SegmentContainer>
      <PointOfContact />
    </div>
  )
}

const mapStateToProps = (state) => ({ user: state.user })
const mapDispatchToProps = (dispatch) => ({
  onOrganisationEdited: (field, value) => dispatch(actions.user.updateOrganisation(field, value)),
})
const SettingsGeneralContainer = connect(mapStateToProps, mapDispatchToProps)(SettingsGeneral)

export default SettingsGeneralContainer
